import { NameHelper } from "@swivl-mono/helpers";
import { EscalationStatus, UserInterface } from "@swivl-mono/types";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { SessionMessageData } from "../../../../../Hooks/useSessionMessageData";
import { Member } from "../../../../../Models/Member/Member.Current";
import PushNotificationsModel, { PushStatus } from "../../../../../Models/PushNotifications/PushNotifications.Model";
import { UserConversationsInterface, UsersModel } from "../../../../../Models/Users/Users.Model";
import EnablePushNotificationsCard from "../Components/Conversations.EnablePushNotificationsCard";
import { OpenConversationCard } from "../Components/Conversations.OpenConversationCard";
import { userPassesLocationFilter } from "../Conversations.LocationFilter";

interface ConversationsOpenUserListProps {
  conversations?: { [userId: number]: UserConversationsInterface };
  sessionMessageData?: { [userId: number]: { [sessionId: number]: SessionMessageData } };
  searchTerm?: string;
  selectedUserId?: number;
  allowedLocationsMap?: { [locationId: string]: boolean };
  allowedGroupIds?: Set<number>;
  hasOrgWideAccess?: boolean;
}

const matchesSearchTerm = (user?: UserInterface, searchTerm?: string) => {
  if (!searchTerm || !searchTerm.length) {
    return true;
  }
  if (!user) return false;
  const term = searchTerm.toLowerCase();
  return NameHelper.nameForUser(user).toLowerCase().includes(term) || Boolean(user.phoneNumber?.toLowerCase().includes(term));
};

const sortConversationsByMostRecentMessage = (conversations: UserConversationsInterface[]) => {
  return conversations.sort((a, b) => {
    const aDate = a.lastUserMessageAt;
    const bDate = b.lastUserMessageAt;
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return bDate.getTime() - aDate.getTime();
  });
};

export function ConversationsOpenUserList(props: ConversationsOpenUserListProps) {
  const pushStatus = PushNotificationsModel.useState((s) => s.pushStatus);
  const conversationsDesktopEnabled = PushNotificationsModel.useState((s) => s.conversationsDesktopEnabled);
  const [isDeferred, setIsDeferred] = useState(sessionStorage.getItem("defer-push-notifications") === "true");

  useEffect(() => {
    PushNotificationsModel.loadConversationsDesktopEnabled();
  }, []);

  const conversationsPushFullyEnabled = pushStatus === PushStatus.ENABLED_AND_REGISTERED && conversationsDesktopEnabled;
  const showPushCta = pushStatus !== PushStatus.LOADING && !isDeferred && !conversationsPushFullyEnabled;
  const { usersOnline } = UsersModel.useState((s) => s);
  const { conversations, searchTerm, selectedUserId, sessionMessageData, allowedLocationsMap, allowedGroupIds, hasOrgWideAccess } = props;

  const { escalatedConversations, otherConversations } = useMemo(() => {
    if (!conversations) return { escalatedConversations: [], otherConversations: [] };

    let escalatedConversations: UserConversationsInterface[] = [];
    let otherConversations: UserConversationsInterface[] = [];

    for (const [_, conversation] of Object.entries(conversations)) {
      if (!allowedLocationsMap) {
        return { escalatedConversations, otherConversations };
      }
      if ((conversation?.user as any)?.deletedDate) {
        console.log("skipping deleted user conversation :", conversation?.user?.id);
        continue; // Skip conversations from locations that are not allowed
      }
      if (!conversation.user) {
        continue;
      }

      if (!userPassesLocationFilter(conversation.user, allowedLocationsMap ?? {}, allowedGroupIds ?? new Set(), hasOrgWideAccess ?? false)) {
        continue;
      }

      if (!matchesSearchTerm(conversation.user, searchTerm)) {
        continue;
      }
      const escalations = conversation.escalations ? Object.values(conversation.escalations) : [];
      if (
        escalations.length > 0 &&
        escalations.some(
          (escalation) =>
            (escalation.status === EscalationStatus.ACTIVE || escalation.status === EscalationStatus.ASSIGNED) &&
            escalation.assignedMemberId === Member.id! &&
            !escalation.resolved
        )
      ) {
        escalatedConversations.push(conversation);
      } else {
        otherConversations.push(conversation);
      }
    }

    escalatedConversations = sortConversationsByMostRecentMessage(escalatedConversations);
    otherConversations = sortConversationsByMostRecentMessage(otherConversations);

    return { escalatedConversations, otherConversations };
  }, [conversations, searchTerm, allowedLocationsMap, allowedGroupIds, hasOrgWideAccess]);

  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20, scale: 0.95 }
  };

  const hasEscalations = (escalatedConversations?.length ?? 0) > 0;

  return (
    <motion.div
      className="h-full overflow-y-auto px-5 py-6 pb-[100px]"
      initial="hidden"
      key="open"
      animate="visible"
      exit="exit"
      variants={fadeVariants}
      transition={{ duration: 0.2 }}
    >
      {/* escalations & open conversations */}
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-grey-600">My Escalations</h2>
        <div className="mb-8">
          <AnimatePresence mode="popLayout">
            {showPushCta && <EnablePushNotificationsCard hasEscalations={hasEscalations} onDefer={() => setIsDeferred(true)} />}
            {escalatedConversations?.length ? (
              escalatedConversations.map((conversation) => (
                <motion.div
                  animate="visible"
                  exit="exit"
                  initial="hidden"
                  variants={cardVariants}
                  layout
                  key={conversation?.user?.id}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Link
                    className="h-fit w-full rounded-md text-left hover:text-black focus-visible:bg-grey-100"
                    to={`/contact-center/conversations/user/${conversation?.user?.id}`}
                  >
                    <OpenConversationCard
                      // sessionMessageData={sessionMessageData}
                      conversation={conversation}
                      key={conversation?.user?.id}
                      selectedUserId={selectedUserId}
                      isEscalated={true}
                      isOnline={conversation?.user?.id ? usersOnline?.[conversation?.user?.id]?.online : false}
                    />
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="rounded-lg bg-grey-100 p-6 text-center" key="noEscalations">
                <p className="text-grey-600">{searchTerm?.length ? "No escalations found" : "Your escalations will appear here"}</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-grey-600">Other Conversations</h2>
        <div className="flex flex-col gap-0.5">
          <AnimatePresence mode="popLayout">
            {otherConversations?.length ? (
              otherConversations.map((conversation) => (
                <motion.div
                  animate="visible"
                  exit="exit"
                  initial="visible"
                  variants={cardVariants}
                  className="h-[58px]"
                  layout
                  key={conversation?.user?.id}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Link
                    className="size-full rounded-md text-left hover:text-black focus-visible:bg-grey-100"
                    to={`/contact-center/conversations/user/${conversation?.user?.id}`}
                  >
                    <OpenConversationCard
                      conversation={conversation}
                      key={conversation?.user?.id}
                      selectedUserId={selectedUserId}
                      isOnline={conversation?.user?.id ? usersOnline?.[conversation?.user?.id]?.online : false}
                      sessionMessageData={sessionMessageData}
                    />
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="rounded-lg bg-grey-100 p-6 text-center" key="noConversations">
                <p className="text-grey-600">{searchTerm?.length ? "No conversations found" : "No open conversations"}</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
